import React from "react";
import OfferingCard from "./OfferingCard";
import config from "../config";
import { registerByEmailHref } from "../data/practice";
import { formatCycle, groupCycleLine } from "../data/content";
import { safeHttpsUrl } from "../services/utils/safeUrl";

/**
 * Free group card (a `groups` content item). A `cycle` schedule shows
 * "Next cycle: ..." until its end date has passed (Bahamas time), then the
 * fallback line pointing to Instagram and Facebook; `none` always shows the
 * fallback; `ongoing` shows neither.
 *
 * Registration (contract v1.3 §3.13.1): a "Sign up" button to `register_url`
 * (her sign-up form; https only, new tab) when there is one, and the
 * "Email … to register" line when `register_by_email` is true. Both can show.
 *
 * @param {object} props
 * @param {string} props.id - The item's slug
 * @param {object} props.group - The item's data (contract §3.13.1 `groups`)
 * @param {string} [props.fallback] - settings notes.group_cycle_fallback
 * @param {Date} [props.now] - Injected clock for tests
 * @param {React.ReactNode} [props.children] - Extra content after the schedule line
 */
export default function GroupCard({ id, group, fallback, now = new Date(), children }) {
  const email = config.business.contactEmail;
  const line = groupCycleLine(group.schedule, now);
  const signUpHref = safeHttpsUrl(group.register_url);
  const cta =
    signUpHref || group.register_by_email ? (
      <>
        {signUpHref ? (
          <a className="btn-cta" href={signUpHref} target="_blank" rel="noopener noreferrer">
            Sign up<span className="sr-only">: {group.title} (opens in a new tab)</span>
          </a>
        ) : null}
        {group.register_by_email ? (
          <p className={signUpHref ? "group-email-line" : undefined}>
            Email <a className="text-link" href={registerByEmailHref(group.title)}>{email}</a> to register.
          </p>
        ) : null}
      </>
    ) : null;

  return (
    <OfferingCard
      id={id}
      title={group.title}
      meta={`${group.price_label} • ${group.audience}`}
      cta={cta}
    >
      <p>{group.description}</p>
      {line === "next" ? (
        <p className="offering-cycle">
          <strong>Next cycle:</strong> {formatCycle(group.schedule)}
        </p>
      ) : null}
      {line === "fallback" && fallback ? <p className="offering-cycle">{fallback}</p> : null}
      {children}
    </OfferingCard>
  );
}
